import axios from 'axios';
import api from './api';
import type { MeasurementUnit } from '@/types/measurement';
import type { OrderItemStatus } from '@/types/orderStatus';
import type { PrintQualityWarning } from './orderService';

const extractBlobErrorMessage = async (error: unknown): Promise<string | null> => {
  if (!axios.isAxiosError(error)) {
    return null;
  }

  const data = error.response?.data;
  if (!(data instanceof Blob)) {
    const message = (data as { message?: unknown } | undefined)?.message;
    return typeof message === 'string' && message.trim().length > 0 ? message : null;
  }

  try {
    const text = await data.text();
    const parsed = JSON.parse(text) as { message?: unknown };
    return typeof parsed.message === 'string' && parsed.message.trim().length > 0
      ? parsed.message
      : null;
  } catch {
    return null;
  }
};

const humanizeAdminExportError = async (
  error: unknown,
  fallbackMessage: string,
): Promise<Error> => {
  if (!axios.isAxiosError(error)) {
    return error instanceof Error ? error : new Error(fallbackMessage);
  }

  const blobMessage = await extractBlobErrorMessage(error);
  if (blobMessage) {
    return new Error(blobMessage);
  }

  if (!error.response) {
    if (error.code === 'ECONNABORTED') {
      return new Error('Export timed out before the server finished preparing the file. Try a smaller group or retry.');
    }

    return new Error('The export connection was interrupted before the server returned a file. The backend may have restarted or run out of memory. Try a smaller export and check server logs.');
  }

  if (error.response.status >= 500) {
    return new Error('The server failed while preparing the export. This can happen when memory is exhausted or the export job crashes. Try a smaller export and check server logs.');
  }

  return error instanceof Error ? error : new Error(fallbackMessage);
};

export type KnowledgeSourceStatus = 'ACTIVE' | 'DELETED' | 'FAILED' | 'IN_PROGRESS';
export type KnowledgeSourceType = 'FILE' | 'URL';

export type AdminKnowledgeSource = {
  readonly id: string;
  readonly sourceType: KnowledgeSourceType;
  readonly uniqueKey: string;
  readonly displayName: string;
  readonly sourceUrl: string | null;
  readonly sourceKey: string;
  readonly status: KnowledgeSourceStatus;
  readonly errorMessage: string | null;
  readonly createdAt: string;
  readonly confirmedAt: string | null;
  readonly updatedAt: string;
};

export type TranslationStatus = 'mt_seeded' | 'failed';

export type AdminOrderItemStatus = OrderItemStatus;

export type AdminOrderStatus =
  | 'NEW'
  | 'PAID'
  | 'IN_PRODUCTION'
  | 'PRINTED'
  | 'IN_DELIVERY'
  | 'DELIVERED'
  | 'RETURNED'
  | 'CANCELLED';

export type AdminShippingProvider = 'UKRPOSHTA' | 'NOVAPOST';
export type AdminOrderExportDpi = 'ORIGINAL' | 'AUTO' | '300' | '600' | '1200' | 'RIP';

export type AdminOrderItemSummary = {
  readonly id: string;
  readonly status: AdminOrderItemStatus;
  readonly updatedAt: string;
};

export type AdminOrderSummary = {
  readonly previewUrls: ReadonlyArray<string>;
  readonly id: string;
  readonly displayCode: string;
  readonly status: AdminOrderStatus;
  readonly fulfillmentMode?: 'SHIPPING' | 'PICKUP';
  readonly createdAt: string;
  readonly confirmedAt: string | null;
  readonly updatedAt: string;
  readonly customer: {
    readonly id: string;
    readonly name: string | null;
    readonly email: string;
  } | null;
  readonly shippingAddress: {
    readonly countryCode: string;
    readonly firstName: string;
    readonly lastName: string;
    readonly phone: string;
    readonly street1: string;
    readonly street2: string | null;
    readonly city: string;
    readonly region: string;
    readonly postalCode: string;
  } | null;
  readonly shippingProvider: string | null;
  readonly ukrposhtaShipmentUuid: string | null;
  readonly ukrposhtaBarcode: string | null;
  readonly novapostShipmentUuid: string | null;
  readonly novapostNumber: string | null;
  readonly shippingAmountUsd: number | null;
  readonly orderAmountUsd: number | null;
  readonly confirmedUsdToUahRate: number | null;
  readonly confirmedUsdToUahEffectiveDate: string | null;
  readonly orderItems: ReadonlyArray<AdminOrderItemSummary>;
};

export type AdminNovapostShipment = {
  readonly uuid: string;
  readonly number: string;
  readonly status: string;
};

export type AdminPaymentTransactionType = 'ORDER_CHARGE' | 'WALLET_TOPUP' | 'ORDER_REFUND' | 'ORDER_REFERRAL_COMMISSION' | 'WALLET_PAYOUT';
export type AdminPaymentStatus = 'PENDING' | 'COMPLETED' | 'FAILED';
export type AdminPaymentDirection = 'DEBIT' | 'CREDIT';

export type AdminPaymentEmailOption = {
  readonly id: string;
  readonly name: string | null;
  readonly email: string;
};

export type AdminPaymentSummaryEntry = {
  readonly currency: string;
  readonly debit: number;
  readonly credit: number;
  readonly total: number;
};

export type AdminPaymentRecord = {
  readonly id: string;
  readonly amount: number;
  readonly signedAmount: number;
  readonly currency: string;
  readonly originalAmount: number | null;
  readonly originalCurrency: string | null;
  readonly status: AdminPaymentStatus;
  readonly provider: string;
  readonly transactionId: string | null;
  readonly transactionType: AdminPaymentTransactionType;
  readonly transactionSource: string;
  readonly direction: AdminPaymentDirection;
  readonly operationTitle: string;
  readonly orderId: string | null;
  readonly order: {
    readonly id: string;
    readonly displayCode: string;
  } | null;
  readonly user: {
    readonly id: string;
    readonly name: string | null;
    readonly email: string;
  };
  readonly processedBy: {
    readonly id: string;
    readonly name: string | null;
    readonly email: string;
  } | null;
  readonly shippingAmountUsd: number | null;
  readonly shippingOriginalAmount: number | null;
  readonly shippingOriginalCurrency: string | null;
  readonly shippingProvider: string | null;
  readonly shippingOption: string | null;
  readonly shippingStatus: string | null;
  readonly reason: string | null;
  readonly needNotify: boolean;
  readonly createdAt: string;
  readonly updatedAt: string;
};

export type AdminPaymentsResponse = {
  readonly data: {
    readonly payments: ReadonlyArray<AdminPaymentRecord>;
    readonly summary: ReadonlyArray<AdminPaymentSummaryEntry>;
    readonly filters: {
      readonly transactionTypes: ReadonlyArray<AdminPaymentTransactionType>;
      readonly statuses: ReadonlyArray<AdminPaymentStatus>;
      readonly emails: ReadonlyArray<AdminPaymentEmailOption>;
    };
    readonly pagination: {
      readonly page: number;
      readonly pageSize: number;
      readonly total: number;
      readonly totalPages: number;
    };
  };
};

export type AdminOrderItem = {
  readonly id: string;
  readonly orderId: string | null;
  readonly status: AdminOrderItemStatus;
  readonly quantity: number | null;
  readonly paperType: {
    readonly id: string;
    readonly name: string;
    readonly brand: string;
    readonly category: string;
    readonly widthIn: number;
    readonly lengthIn: number;
    readonly isRoll: boolean;
  } | null;
  readonly printer: {
    readonly id: string;
    readonly name: string;
    readonly brand: string;
    readonly model: string;
  } | null;
  readonly widthIn: number | null;
  readonly heightIn: number | null;
  readonly marginIn: number | null;
  readonly galleryWrapIn: number | null;
  readonly galleryWrapMirrorEdges: boolean;
  readonly printQualityWarning?: PrintQualityWarning | null;
  readonly colorMode: string | null;
  readonly priceUsd: number | null;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly order: {
    readonly id: string;
    readonly displayCode: string;
    readonly status: string;
    readonly shippingProvider: string | null;
    readonly trackingNumber: string | null;
    readonly packaging: string | null;
    readonly comment: string | null;
    readonly createdAt: string;
    readonly confirmedAt: string | null;
    readonly updatedAt: string;
  } | null;
  readonly customer: {
    readonly id: string;
    readonly name: string | null;
    readonly email: string;
  } | null;
  readonly shippingAddress: {
    readonly countryCode: string;
    readonly firstName: string;
    readonly lastName: string;
    readonly phone: string;
    readonly street1: string;
    readonly street2: string | null;
    readonly city: string;
    readonly region: string;
    readonly postalCode: string;
  } | null;
  readonly uploadedFile: {
    readonly id: string;
    readonly filename: string;
    readonly mimeType: string | null;
    readonly sizeBytes: number;
    readonly originalWidthPx: number | null;
    readonly originalHeightPx: number | null;
    readonly dpiX: number | null;
    readonly dpiY: number | null;
    readonly signedUrl: string | null;
    readonly thumbnailSignedUrl: string | null;
    readonly downloadUrl: string | null;
  } | null;
};

type AdminOrderItemsResponse = {
  readonly data: {
    readonly items: ReadonlyArray<AdminOrderItem>;
  };
};

type AdminOrderItemStatusResponse = {
  readonly data: {
    readonly id: string;
    readonly status: AdminOrderItemStatus;
    readonly updatedAt: string;
  };
};

type AdminOrdersResponse = {
  readonly data: {
    readonly orders: ReadonlyArray<AdminOrderSummary>;
  };
};

type AdminOrderResponse = {
  readonly data: {
    readonly order: AdminOrderSummary;
  };
};

export type AdminOrderExportResult = {
  readonly blob: Blob;
  readonly filename: string;
};

export type AdminOrderExportGroupInput = {
  readonly itemIds: ReadonlyArray<string>;
  readonly targetDpi: AdminOrderExportDpi;
};

export type AdminOrderItemDownloadResult = {
  readonly downloadUrl: string;
  readonly filename: string;
  readonly sizeBytes: number;
  readonly expiresAt: string;
  readonly item: AdminOrderItemSummary;
};

type AdminOrderItemDownloadResponse = {
  readonly data: AdminOrderItemDownloadResult;
};

export type AdminDateWindowParams = {
  readonly dateFrom?: string;
  readonly dateTo?: string;
};

export type AdminUserActivitySummary = {
  readonly id: string;
  readonly participantType: 'registered' | 'temporary';
  readonly userId: string | null;
  readonly temporaryUserId: string | null;
  readonly name: string | null;
  readonly email: string | null;
  readonly lastActivity: string | null;
  readonly hasHumanSupportRequest?: boolean;
};

export type AdminUserChatLog = {
  readonly id: string;
  readonly role: 'assistant' | 'user' | 'interface' | 'supervisor' | 'admin';
  readonly content: string;
  readonly mode?: 'AI' | 'WAITING_FOR_AGENT' | 'HUMAN';
  readonly createdAt: string;
};

export type AdminUserAnalyticsEvent = {
  readonly id: string;
  readonly eventName: string;
  readonly eventTimestamp: string;
  readonly screen: string | null;
  readonly appName: string | null;
  readonly sessionId: string | null;
  readonly properties: unknown;
  readonly context: unknown;
};

export type AdminTelegramDeliveryResult = {
  readonly configured: boolean;
  readonly skipped: boolean;
  readonly attemptedCount: number;
  readonly deliveredCount: number;
  readonly failedCount: number;
  readonly results: ReadonlyArray<{
    readonly chatId: string;
    readonly delivered: boolean;
    readonly errorMessage: string | null;
  }>;
  readonly sentAt: string;
};

export type AdminTelegramReinitializeResult = {
  readonly configured: boolean;
  readonly recipientCount: number;
  readonly botUsername: string | null;
  readonly checkedAt: string;
  readonly errorMessage: string | null;
};

export type AdminTelegramHealthResult = {
  readonly environment: string;
  readonly configured: boolean;
  readonly hasBotToken: boolean;
  readonly recipientCount: number;
  readonly results: ReadonlyArray<{
    readonly chatId: string;
    readonly delivered: boolean;
    readonly errorMessage: string | null;
  }>;
  readonly checkedAt: string;
};

type AdminUserActivityUsersResponse = {
  readonly data: {
    readonly users: ReadonlyArray<AdminUserActivitySummary>;
  };
};

type AdminUserChatLogsResponse = {
  readonly data: {
    readonly logs: ReadonlyArray<AdminUserChatLog>;
  };
};

type AdminUserAnalyticsEventsResponse = {
  readonly data: {
    readonly events: ReadonlyArray<AdminUserAnalyticsEvent>;
  };
};

type AdminTelegramTestResponse = {
  readonly data: AdminTelegramDeliveryResult;
};

type AdminTelegramReinitializeResponse = {
  readonly data: AdminTelegramReinitializeResult;
};

type AdminTelegramHealthResponse = {
  readonly data: AdminTelegramHealthResult;
};

export type AdminTranslationLocale = {
  readonly locale: string;
  readonly description: string | null;
  readonly status: TranslationStatus | null;
  readonly provider: 'ai' | 'human' | null;
  readonly hashCodeDescription: string | null;
  readonly updatedAt: string | null;
};

export type AdminPaperTranslation = {
  readonly id: string;
  readonly name: string;
  readonly brand: string;
  readonly category: string;
  readonly englishDescription: string;
  readonly englishHash: string;
  readonly translations: ReadonlyArray<AdminTranslationLocale>;
};

export type AdminPrinterTranslation = {
  readonly id: string;
  readonly name: string;
  readonly brand: string;
  readonly model: string;
  readonly englishDescription: string;
  readonly englishHash: string;
  readonly translations: ReadonlyArray<AdminTranslationLocale>;
};

export type AdminPackagingMaterial = {
  readonly id: string;
  readonly name: string;
  readonly sku: string;
  readonly type: 'BOX' | 'TUBE';
  readonly fitStrategy: 'flat' | 'roll';
  readonly isActive: boolean;
  readonly weightGrams: number;
  readonly costUsd: number;
  readonly extraPerItemPackagingCostUsd: number;
  readonly oneTimePackagingOverheadCostUsd: number;
  readonly extraPerItemPackagingWeightGrams: number;
  readonly oneTimePackagingOverheadWeightGrams: number;
  readonly innerWidthIn: number | null;
  readonly innerHeightIn: number | null;
  readonly innerDepthIn: number | null;
  readonly baseLengthIn: number | null;
  readonly crossSectionIn: number | null;
  readonly cutAllowanceIn: number | null;
  readonly minCutLengthIn: number | null;
  readonly createdAt: string;
  readonly updatedAt: string;
};

export type AdminPaperType = {
  readonly id: string;
  readonly name: string;
  readonly displayCode: string | null;
  readonly brand: string;
  readonly category: 'FINEART' | 'STANDARD' | 'ECONOMY';
  readonly mediaType: 'PAPER' | 'CANVAS';
  readonly weightGsm: number;
  readonly widthIn: number;
  readonly lengthIn: number;
  readonly isRoll: boolean;
  readonly available: boolean;
  readonly description: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
};

export type AdminHomepageSliderImage = {
  readonly id: string;
  readonly imageUrl: string;
  readonly thumbnailUrl: string | null;
  readonly originalFilename: string;
  readonly originalSizeBytes: number;
  readonly desktopSizeBytes: number;
  readonly thumbnailSizeBytes: number | null;
  readonly widthPx: number | null;
  readonly heightPx: number | null;
  readonly altText: string;
  readonly title: string | null;
  readonly caption: string | null;
  readonly linkUrl: string | null;
  readonly sortOrder: number;
  readonly isActive: boolean;
  readonly createdAt: string;
  readonly updatedAt: string;
};

export type AdminPromotion = {
  readonly id: string;
  readonly name: string;
  readonly description: string | null;
  readonly code: string | null;
  readonly codeNorm: string | null;
  readonly discountType: 'PERCENT' | 'FIXED_AMOUNT';
  readonly discountValue: number | null;
  readonly currency: string | null;
  readonly startsAt: string | null;
  readonly endsAt: string | null;
  readonly isActive: boolean;
  readonly stackable: boolean;
  readonly usageLimitTotal: number | null;
  readonly usageLimitPerUser: number | null;
  readonly rules: unknown;
  readonly usageCount: number;
  readonly totalDiscountUsd: number;
  readonly lastRedeemedAt: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
};

export type AdminPromotionInput = {
  readonly name: string;
  readonly description?: string | null;
  readonly code?: string | null;
  readonly discountType: 'PERCENT' | 'FIXED_AMOUNT';
  readonly discountValue: number;
  readonly currency?: string | null;
  readonly startsAt?: string | null;
  readonly endsAt?: string | null;
  readonly isActive?: boolean;
  readonly stackable?: boolean;
  readonly usageLimitTotal?: number | null;
  readonly usageLimitPerUser?: number | null;
  readonly rules?: unknown;
};

export type AdminPromotionUpdateInput = Partial<AdminPromotionInput>;

type AdminPackagingMaterialsResponse = {
  readonly data: ReadonlyArray<AdminPackagingMaterial>;
};

type AdminPackagingMaterialResponse = {
  readonly data: AdminPackagingMaterial;
};

type AdminPaperTypesResponse = {
  readonly data: ReadonlyArray<AdminPaperType>;
};

type AdminPaperTypeResponse = {
  readonly data: AdminPaperType;
};

type AdminPromotionsResponse = {
  readonly data: ReadonlyArray<AdminPromotion>;
};

type AdminPromotionResponse = {
  readonly data: AdminPromotion;
};

export type AdminPackagingMaterialInput = {
  readonly name: string;
  readonly sku: string;
  readonly type: 'BOX' | 'TUBE';
  readonly fitStrategy: 'flat' | 'roll';
  readonly isActive?: boolean;
  readonly weightGrams: number;
  readonly costUsd: number;
  readonly extraPerItemPackagingCostUsd: number;
  readonly oneTimePackagingOverheadCostUsd: number;
  readonly extraPerItemPackagingWeightGrams: number;
  readonly oneTimePackagingOverheadWeightGrams: number;
  readonly innerWidthIn?: number | null;
  readonly innerHeightIn?: number | null;
  readonly innerDepthIn?: number | null;
  readonly baseLengthIn?: number | null;
  readonly crossSectionIn?: number | null;
  readonly cutAllowanceIn?: number | null;
  readonly minCutLengthIn?: number | null;
};

export type AdminPackagingMaterialUpdateInput = Partial<AdminPackagingMaterialInput>;

export type AdminPaperTypeInput = {
  readonly name: string;
  readonly displayCode?: string | null;
  readonly brand: string;
  readonly category: 'FINEART' | 'STANDARD' | 'ECONOMY';
  readonly mediaType: 'PAPER' | 'CANVAS';
  readonly weightGsm: number;
  readonly widthIn: number;
  readonly lengthIn: number;
  readonly isRoll: boolean;
  readonly available: boolean;
  readonly description?: string | null;
};

export type AdminPaperTypeUpdateInput = Partial<AdminPaperTypeInput>;

export type TranslationReconciliationSummary = {
  readonly tasksCreated: number;
  readonly itemsProcessed: number;
};

export type TranslationWorkerSummary = {
  readonly alreadyRunning: boolean;
  readonly batchesProcessed: number;
  readonly tasksAttempted: number;
};

export type AdminExchangeRate = {
  readonly id: string;
  readonly baseCurrency: string;
  readonly quoteCurrency: string;
  readonly source: string;
  readonly rate: number;
  readonly effectiveDate: string;
  readonly fetchedAt: string;
  readonly createdAt: string;
};

export type AdminNovapostCacheState = 'MISSING' | 'WARMING' | 'READY' | 'STALE' | 'FAILED';
export type AdminNovapostSyncStatus = 'RUNNING' | 'SUCCESS' | 'FAILED';
export type AdminNovapostSyncTrigger = 'STARTUP' | 'SCHEDULED' | 'ADMIN_MANUAL';

export type AdminNovapostCacheStatus = {
  readonly countryCode: string;
  readonly state: AdminNovapostCacheState;
  readonly isRunning: boolean;
  readonly isQueued: boolean;
  readonly lastSuccessfulSyncAt: string | null;
  readonly lastFinishedSyncAt: string | null;
  readonly lastDurationMs: number | null;
  readonly activeSnapshotDivisionCount: number;
  readonly activeSnapshotSettlementCount: number;
  readonly activeSnapshotActivatedAt: string | null;
  readonly currentRunId: string | null;
  readonly currentRunStartedAt: string | null;
  readonly latestRunStatus: AdminNovapostSyncStatus | null;
  readonly latestRunTrigger: AdminNovapostSyncTrigger | null;
  readonly latestRunErrorMessage: string | null;
};

export type AdminNovapostCachedDivision = {
  readonly id: string;
  readonly countryCode: string;
  readonly sourceDivisionId: number;
  readonly settlementId: number | null;
  readonly settlementName: string | null;
  readonly settlementRegionName: string | null;
  readonly divisionName: string | null;
  readonly shortName: string | null;
  readonly number: string | null;
  readonly address: string | null;
  readonly divisionCategory: string | null;
  readonly divisionStatus: string | null;
  readonly customerServiceAvailable: boolean | null;
  readonly prohibitedSending: boolean | null;
  readonly prohibitedIssuance: boolean | null;
  readonly rawPayload: unknown;
};

type AdminExchangeRatesResponse = {
  readonly data: {
    readonly exchangeRates: ReadonlyArray<AdminExchangeRate>;
  };
};

type ExchangeRateRefreshResponse = {
  readonly data: {
    readonly processedPairs: number;
    readonly persistedPairs: number;
    readonly skippedPairs: ReadonlyArray<{ pair: { baseCurrency: string; quoteCurrency: string }; reason: string }>;
    readonly source: string;
    readonly effectiveDate?: string;
  };
};

type AdminNovapostCacheStatusesResponse = {
  readonly data: {
    readonly statuses: ReadonlyArray<AdminNovapostCacheStatus>;
  };
};

type AdminNovapostCachedDivisionsResponse = {
  readonly data: {
    readonly divisions: ReadonlyArray<AdminNovapostCachedDivision>;
  };
};

type AdminNovapostRefreshResult = {
  readonly countryCode: string;
  readonly alreadyRunning: boolean;
  readonly queued: boolean;
  readonly triggerSource: AdminNovapostSyncTrigger;
  readonly blockedByActiveQueue?: boolean;
};

type AdminNovapostRefreshCountryResponse = {
  readonly data: AdminNovapostRefreshResult;
};

type AdminNovapostRefreshAllResponse = {
  readonly data: {
    readonly queued: ReadonlyArray<AdminNovapostRefreshResult>;
    readonly queuedCount: number;
    readonly alreadyRunningCount: number;
  };
};

type AdminNovapostStopResponse = {
  readonly data: {
    readonly queuedCleared: number;
    readonly runningSignalled: number;
  };
};

type TranslationReconciliationResponse = {
  readonly data: TranslationReconciliationSummary;
};

type TranslationWorkerRunResponse = {
  readonly data: TranslationWorkerSummary;
};

type AdminPaperTranslationsResponse = {
  readonly data: ReadonlyArray<AdminPaperTranslation>;
};

type AdminPrinterTranslationsResponse = {
  readonly data: ReadonlyArray<AdminPrinterTranslation>;
};

type ManualUpdateResponse = {
  readonly data: {
    readonly entityId: string;
    readonly locale: string;
    readonly translation: {
      readonly description: string;
      readonly status: TranslationStatus;
      readonly provider: 'ai' | 'human';
      readonly hashCodeDescription: string;
      readonly updatedAt: string;
    };
  };
};

export type AdminOrderRefundDetails = {
  readonly id: string;
  readonly displayCode: string;
  readonly status: AdminOrderStatus;
  readonly createdAt: string;
  readonly confirmedAt: string | null;
  readonly updatedAt: string;
  readonly customer: {
    readonly id: string;
    readonly name: string | null;
    readonly email: string;
  } | null;
  readonly shippingAddress: {
    readonly countryCode: string;
    readonly firstName: string;
    readonly lastName: string;
    readonly phone: string;
    readonly street1: string;
    readonly street2: string | null;
    readonly city: string;
    readonly region: string;
    readonly postalCode: string;
  } | null;
  readonly shippingAmountUsd: number | null;
  readonly orderAmountUsd: number | null;
  readonly confirmedUsdToUahRate: number | null;
  readonly confirmedUsdToUahEffectiveDate: string | null;
};

export type AdminUkrposhtaShipmentSummary = {
  readonly orderId: string;
  readonly displayCode: string;
  readonly orderStatus: AdminOrderStatus;
  readonly fulfillmentMode?: 'SHIPPING' | 'PICKUP';
  readonly createdAt: string;
  readonly confirmedAt: string | null;
  readonly updatedAt: string;
  readonly shipmentUuid: string | null;
  readonly barcode: string | null;
  readonly shipmentStatus: string | null;
  readonly shippingProvider: string | null;
  readonly shippingOption: string | null;
  readonly shippingActualAmount: number | null;
  readonly shippingActualCurrency: string | null;
  readonly shippingAmountUsd: number | null;
  readonly shippingOriginalAmount: number | null;
  readonly shippingOriginalCurrency: string | null;
  readonly totalOrderCostAmount: number | null;
  readonly totalOrderCostCurrency: string | null;
  readonly walletTopUpOriginalAmount: number | null;
  readonly walletTopUpOriginalCurrency: string | null;
  readonly confirmedUsdToUahRate: number | null;
  readonly confirmedUsdToUahEffectiveDate: string | null;
  readonly printedItemsCostUsd: number | null;
  readonly userDiscountLevel: {
    readonly type: string;
    readonly percent: number;
  } | null;
  readonly packaging: string | null;
  readonly packagingType: string | null;
  readonly packagingSku: string | null;
  readonly customer: {
    readonly id: string;
    readonly name: string | null;
    readonly email: string;
  } | null;
  readonly recipient: {
    readonly firstName: string;
    readonly lastName: string;
    readonly phone: string;
    readonly countryCode: string;
    readonly region: string;
    readonly city: string;
    readonly street1: string;
    readonly street2: string | null;
    readonly postalCode: string;
  } | null;
  readonly sender: {
    readonly name: string;
    readonly phone: string | null;
    readonly address: {
      readonly postcode: string;
      readonly region: string;
      readonly city: string;
      readonly street: string;
      readonly houseNumber?: string;
    };
  };
  readonly shipmentProfile: {
    readonly type: string | null;
    readonly packageType: string | null;
    readonly deliveryType: string | null;
    readonly transportType: string | null;
    readonly categoryType: string | null;
    readonly weight: number | null;
    readonly length: number | null;
    readonly width: number | null;
    readonly height: number | null;
    readonly tracked: boolean | null;
  };
  readonly parcelItems: ReadonlyArray<{
    readonly orderItemId: string;
    readonly itemName: string | null;
    readonly latinName: string | null;
    readonly description: string | null;
    readonly quantity: number | null;
    readonly value: number | null;
    readonly currencyCode: string | null;
    readonly weight: number | null;
    readonly countryOfOrigin: string | null;
    readonly hsCode: string | null;
    readonly priceUsd: number | null;
    readonly totalWidthIn: number | null;
    readonly totalHeightIn: number | null;
  }>;
};

export type AdminRefundReason =
  | 'refund shipment cost'
  | 'refund whole order'
  | 'refund due custom reason';

export type AdminRefundRequest = {
  readonly amount: number;
  readonly reason: AdminRefundReason;
  readonly customReason?: string;
  readonly informUser?: boolean;
};

export type AdminRefundResponse = {
  readonly refund: {
    readonly id: string;
    readonly orderId: string | null;
    readonly amount: number;
    readonly currency: string;
    readonly reason: string | null;
    readonly processedById: string | null;
    readonly createdAt: string;
  };
  readonly telegramNotification: {
    readonly sent: boolean;
    readonly deliveredCount: number;
  };
};

type AdminRefundApiResponse = {
  readonly data: AdminRefundResponse;
};

type AdminKnowledgeSourcesResponse = {
  readonly sources: ReadonlyArray<AdminKnowledgeSource>;
};

type AdminKnowledgeSourceResponse = {
  readonly source: AdminKnowledgeSource;
};

type AdminHomepageSliderImagesResponse = {
  readonly data: {
    readonly images: ReadonlyArray<AdminHomepageSliderImage>;
  };
};

type AdminHomepageSliderImageResponse = {
  readonly data: {
    readonly image: AdminHomepageSliderImage;
  };
};

export const fetchKnowledgeSources = async (): Promise<ReadonlyArray<AdminKnowledgeSource>> => {
  const response = await api.get<AdminKnowledgeSourcesResponse>('/admin/knowledge-base/sources');
  return response.data.sources;
};

export const uploadKnowledgeDocument = async (file: File): Promise<AdminKnowledgeSource> => {
  const formData = new FormData();
  formData.append('document', file, file.name);
  const response = await api.post<AdminKnowledgeSourceResponse>(
    '/admin/knowledge-base/sources/file',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return response.data.source;
};

export const uploadKnowledgeUrl = async (url: string): Promise<AdminKnowledgeSource> => {
  const response = await api.post<AdminKnowledgeSourceResponse>('/admin/knowledge-base/sources/url', { url });
  return response.data.source;
};

export const deleteKnowledgeSource = async (sourceId: string): Promise<AdminKnowledgeSource> => {
  const response = await api.delete<AdminKnowledgeSourceResponse>(`/admin/knowledge-base/sources/${sourceId}`);
  return response.data.source;
};

export const reingestKnowledgeSource = async (sourceId: string): Promise<AdminKnowledgeSource> => {
  const response = await api.post<AdminKnowledgeSourceResponse>(`/admin/knowledge-base/sources/${sourceId}/reingest`);
  return response.data.source;
};

export const fetchPaidOrderItems = async (params: AdminDateWindowParams = {}): Promise<ReadonlyArray<AdminOrderItem>> => {
  const response = await api.get<AdminOrderItemsResponse>('/admin/order-items/paid', {
    params: {
      ...(params.dateFrom ? { dateFrom: params.dateFrom } : {}),
      ...(params.dateTo ? { dateTo: params.dateTo } : {}),
    },
  });
  return response.data.data.items;
};

export const fetchAdminPayments = async (params: {
  page?: number;
  pageSize?: number;
  search?: string;
  email?: string;
  transactionType?: string;
  status?: string;
} = {}): Promise<AdminPaymentsResponse['data']> => {
  const response = await api.get<AdminPaymentsResponse>('/admin/payments', { params });
  return response.data.data;
};

export const fetchAdminOrders = async (params: AdminDateWindowParams = {}): Promise<ReadonlyArray<AdminOrderSummary>> => {
  const response = await api.get<AdminOrdersResponse>('/admin/orders', {
    params: {
      ...(params.dateFrom ? { dateFrom: params.dateFrom } : {}),
      ...(params.dateTo ? { dateTo: params.dateTo } : {}),
    },
  });
  return response.data.data.orders;
};

export const updateAdminOrderShippingTracking = async (
  orderId: string,
  payload: {
    readonly provider: AdminShippingProvider;
    readonly trackingNumber: string;
    readonly shipmentUuid?: string;
  },
): Promise<AdminOrderSummary> => {
  const response = await api.patch<AdminOrderResponse>(`/admin/orders/${orderId}/shipping-tracking`, payload);
  return response.data.data.order;
};

export const updateAdminOrderStatus = async (
  orderId: string,
  status: Extract<AdminOrderStatus, 'PAID' | 'IN_PRODUCTION' | 'PRINTED' | 'IN_DELIVERY' | 'DELIVERED'>,
): Promise<AdminOrderSummary> => {
  const response = await api.patch<AdminOrderResponse>(`/admin/orders/${orderId}/status`, { status });
  return response.data.data.order;
};

export const createUkrposhtaShipment = async (orderId: string): Promise<any> => {
  const response = await api.post(`/admin/orders/${orderId}/ukrposhta/shipment`);
  return response.data.data;
};

export const downloadUkrposhtaLabel = async (shipmentUuid: string): Promise<void> => {
  try {
    const response = await api.get(`/admin/orders/ukrposhta/label/${shipmentUuid}`, {
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `ukrposhta_label_${shipmentUuid}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    const message = await extractBlobErrorMessage(error);
    if (message) {
      throw new Error(message);
    }
    throw error;
  }
};

export const createNovapostShipment = async (orderId: string): Promise<AdminNovapostShipment> => {
  const response = await api.post<{ data: AdminNovapostShipment }>(`/admin/orders/${orderId}/novapost/shipment`);
  return response.data.data;
};

export const downloadNovapostLabel = async (shipmentUuid: string): Promise<void> => {
  try {
    const response = await api.get(`/admin/orders/novapost/label/${shipmentUuid}`, {
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `novapost_label_${shipmentUuid}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    const message = await extractBlobErrorMessage(error);
    if (message) {
      throw new Error(message);
    }
    throw error;
  }
};

export const updateAdminOrderItemStatus = async (
  orderItemId: string,
  status: AdminOrderItemStatus,
  comment?: string,
): Promise<AdminOrderItemStatusResponse['data']> => {
  const response = await api.patch<AdminOrderItemStatusResponse>(`/admin/order-items/${orderItemId}`, {
    status,
    ...(typeof comment === 'string' && comment.trim().length > 0 ? { comment } : {}),
  });
  return response.data.data;
};

export const exportOrderItemsZip = async (
  itemIds: ReadonlyArray<string>,
  targetDpi: AdminOrderExportDpi = 'AUTO',
  measurementUnit: MeasurementUnit = 'in',
  exportGroups?: ReadonlyArray<AdminOrderExportGroupInput>,
): Promise<AdminOrderExportResult> => {
  try {
    const response = await api.post('/admin/order-items/export', { itemIds, targetDpi, measurementUnit, exportGroups }, { responseType: 'blob' });
    const disposition = typeof response.headers?.['content-disposition'] === 'string'
      ? response.headers['content-disposition']
      : '';
    const match = disposition.match(/filename=\"?([^\"]+)\"?/i);
    const filename = match?.[1] ?? 'print_export.zip';
    return {
      blob: response.data as Blob,
      filename,
    };
  } catch (error) {
    throw await humanizeAdminExportError(error, 'Failed to export print order items.');
  }
};

export const downloadOrderItemWithMargin = async (
  orderItemId: string,
  measurementUnit: MeasurementUnit = 'in',
  targetDpi: AdminOrderExportDpi = 'AUTO',
): Promise<AdminOrderItemDownloadResult> => {
  try {
    const response = await api.get<AdminOrderItemDownloadResponse>(`/admin/order-items/${orderItemId}/download-with-margin`, {
      params: { outputFormat: 'tiff', measurementUnit, targetDpi },
    });
    return response.data.data;
  } catch (error) {
    throw await humanizeAdminExportError(error, 'Failed to prepare the print-ready file.');
  }
};

export const fetchUserActivityUsers = async (): Promise<ReadonlyArray<AdminUserActivitySummary>> => {
  const response = await api.get<AdminUserActivityUsersResponse>('/admin/user-activity/users');
  return response.data.data.users;
};

export const fetchUserChatLogs = async (
  identity: { userId?: string | null; temporaryUserId?: string | null },
  limit = 200
): Promise<ReadonlyArray<AdminUserChatLog>> => {
  const response = await api.get<AdminUserChatLogsResponse>('/admin/user-activity/chat', {
    params: {
      limit,
      userId: identity.userId ?? undefined,
      temporaryUserId: identity.temporaryUserId ?? undefined,
    },
  });
  return response.data.data.logs;
};

export const fetchUserAnalyticsEvents = async (
  identity: { userId?: string | null; temporaryUserId?: string | null },
  limit = 100
): Promise<ReadonlyArray<AdminUserAnalyticsEvent>> => {
  const response = await api.get<AdminUserAnalyticsEventsResponse>('/admin/user-activity/events', {
    params: {
      limit,
      userId: identity.userId ?? undefined,
      temporaryUserId: identity.temporaryUserId ?? undefined,
    },
  });
  return response.data.data.events;
};

export const adminSendMessage = async (
  targetUserId: string | null,
  targetTemporaryUserId: string | null,
  content: string
): Promise<{ success: boolean }> => {
  const response = await api.post<{ success: boolean }>('/admin/user-activity/users/chat/send', {
    targetUserId,
    targetTemporaryUserId,
    content,
  });
  return response.data;
};

export const adminSetHumanSupportMode = async (
  targetUserId: string | null,
  targetTemporaryUserId: string | null,
  enabled: boolean
): Promise<{ success: boolean; mode: 'AI' | 'HUMAN' }> => {
  const response = await api.post<{ success: boolean; mode: 'AI' | 'HUMAN' }>('/admin/user-activity/users/chat/human-support-mode', {
    targetUserId,
    targetTemporaryUserId,
    enabled,
  });
  return response.data;
};

export const triggerTranslationReconciliation = async (): Promise<TranslationReconciliationSummary> => {
  const response = await api.post<TranslationReconciliationResponse>('/admin/translations/reconcile');
  return response.data.data;
};

export const triggerTranslationWorkerRun = async (): Promise<TranslationWorkerSummary> => {
  const response = await api.post<TranslationWorkerRunResponse>('/admin/translations/run-worker');
  return response.data.data;
};

export interface FetchAdminExchangeRatesParams {
  readonly baseCurrency?: string;
  readonly quoteCurrency?: string;
  readonly source?: string;
  readonly startDate?: string;
  readonly endDate?: string;
  readonly limit?: number;
}

export const fetchAdminExchangeRates = async (
  params: FetchAdminExchangeRatesParams = {},
): Promise<ReadonlyArray<AdminExchangeRate>> => {
  const response = await api.get<AdminExchangeRatesResponse>('/admin/exchange-rates', {
    params,
  });
  return response.data.data.exchangeRates;
};

export const refreshAdminExchangeRates = async (
  pairs?: ReadonlyArray<{ baseCurrency: string; quoteCurrency: string }>,
): Promise<ExchangeRateRefreshResponse['data']> => {
  const response = await api.post<ExchangeRateRefreshResponse>('/admin/exchange-rates/refresh', {
    pairs,
  });
  return response.data.data;
};

export const fetchAdminNovapostCacheStatuses = async (): Promise<ReadonlyArray<AdminNovapostCacheStatus>> => {
  const response = await api.get<AdminNovapostCacheStatusesResponse>('/admin/novapost-cache/statuses');
  return response.data.data.statuses;
};

export interface FetchAdminNovapostCachedDivisionsParams {
  readonly countryCode?: string;
  readonly query?: string;
  readonly category?: string;
  readonly status?: string;
  readonly limit?: number;
}

export const fetchAdminNovapostCachedDivisions = async (
  params: FetchAdminNovapostCachedDivisionsParams = {},
): Promise<ReadonlyArray<AdminNovapostCachedDivision>> => {
  const response = await api.get<AdminNovapostCachedDivisionsResponse>('/admin/novapost-cache/divisions', {
    params,
  });
  return response.data.data.divisions;
};

export const refreshAllAdminNovapostCaches = async (): Promise<AdminNovapostRefreshAllResponse['data']> => {
  const response = await api.post<AdminNovapostRefreshAllResponse>('/admin/novapost-cache/refresh-all');
  return response.data.data;
};

export const refreshAdminNovapostCountryCache = async (
  countryCode: string,
): Promise<AdminNovapostRefreshResult> => {
  const response = await api.post<AdminNovapostRefreshCountryResponse>(`/admin/novapost-cache/refresh/${countryCode}`);
  return response.data.data;
};

export const stopAdminNovapostCacheRefresh = async (): Promise<AdminNovapostStopResponse['data']> => {
  const response = await api.post<AdminNovapostStopResponse>('/admin/novapost-cache/stop');
  return response.data.data;
};

export const sendAdminTelegramTestMessage = async (): Promise<AdminTelegramDeliveryResult> => {
  const response = await api.post<AdminTelegramTestResponse>('/admin/telegram/test');
  return response.data.data;
};

export const reinitializeAdminTelegram = async (): Promise<AdminTelegramReinitializeResult> => {
  const response = await api.post<AdminTelegramReinitializeResponse>('/admin/telegram/reinitialize');
  return response.data.data;
};

export const getAdminTelegramHealth = async (): Promise<AdminTelegramHealthResult> => {
  const response = await api.get<AdminTelegramHealthResponse>('/admin/telegram/health');
  return response.data.data;
};

export const fetchPackagingMaterials = async (): Promise<ReadonlyArray<AdminPackagingMaterial>> => {
  const response = await api.get<AdminPackagingMaterialsResponse>('/admin/packaging-materials');
  return response.data.data;
};

export const fetchAdminPaperTypes = async (): Promise<ReadonlyArray<AdminPaperType>> => {
  const response = await api.get<AdminPaperTypesResponse>('/admin/paper-types');
  return response.data.data;
};

export const createAdminPaperType = async (payload: AdminPaperTypeInput): Promise<AdminPaperType> => {
  const response = await api.post<AdminPaperTypeResponse>('/admin/paper-types', payload);
  return response.data.data;
};

export const updateAdminPaperType = async (id: string, payload: AdminPaperTypeUpdateInput): Promise<AdminPaperType> => {
  const response = await api.put<AdminPaperTypeResponse>('/admin/paper-types/' + id, payload);
  return response.data.data;
};

export const fetchPackagingMaterialById = async (id: string): Promise<AdminPackagingMaterial> => {
  const response = await api.get<AdminPackagingMaterialResponse>(`/admin/packaging-materials/${id}`);
  return response.data.data;
};

export const createPackagingMaterial = async (
  payload: AdminPackagingMaterialInput,
): Promise<AdminPackagingMaterial> => {
  const response = await api.post<AdminPackagingMaterialResponse>('/admin/packaging-materials', payload);
  return response.data.data;
};

export const updatePackagingMaterial = async (
  id: string,
  payload: AdminPackagingMaterialUpdateInput,
): Promise<AdminPackagingMaterial> => {
  const response = await api.put<AdminPackagingMaterialResponse>(`/admin/packaging-materials/${id}`, payload);
  return response.data.data;
};

export const updatePackagingMaterialActivation = async (
  id: string,
  isActive: boolean,
): Promise<AdminPackagingMaterial> => {
  const response = await api.patch<AdminPackagingMaterialResponse>(
    `/admin/packaging-materials/${id}/activation`,
    { isActive },
  );
  return response.data.data;
};

export const fetchAdminPromotions = async (): Promise<ReadonlyArray<AdminPromotion>> => {
  const response = await api.get<AdminPromotionsResponse>('/admin/promotions');
  return response.data.data;
};

export const createAdminPromotion = async (payload: AdminPromotionInput): Promise<AdminPromotion> => {
  const response = await api.post<AdminPromotionResponse>('/admin/promotions', payload);
  return response.data.data;
};

export const updateAdminPromotion = async (
  id: string,
  payload: AdminPromotionUpdateInput,
): Promise<AdminPromotion> => {
  const response = await api.put<AdminPromotionResponse>(`/admin/promotions/${id}`, payload);
  return response.data.data;
};

export const closeAdminPromotion = async (id: string): Promise<AdminPromotion> => {
  const response = await api.patch<AdminPromotionResponse>(`/admin/promotions/${id}/close`);
  return response.data.data;
};

export const fetchAdminHomepageSliderImages = async (): Promise<ReadonlyArray<AdminHomepageSliderImage>> => {
  const response = await api.get<AdminHomepageSliderImagesResponse>('/admin/homepage-slider-images');
  return response.data.data.images;
};

export const createAdminHomepageSliderImage = async (payload: {
  readonly file: File;
  readonly altText: string;
  readonly title?: string;
  readonly caption?: string;
  readonly linkUrl?: string;
  readonly sortOrder?: number;
  readonly isActive?: boolean;
}): Promise<AdminHomepageSliderImage> => {
  const formData = new FormData();
  formData.append('image', payload.file, payload.file.name);
  formData.append('altText', payload.altText);
  formData.append('title', payload.title ?? '');
  formData.append('caption', payload.caption ?? '');
  formData.append('linkUrl', payload.linkUrl ?? '');
  formData.append('sortOrder', String(payload.sortOrder ?? 0));
  formData.append('isActive', String(payload.isActive ?? true));

  const response = await api.post<AdminHomepageSliderImageResponse>(
    '/admin/homepage-slider-images',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return response.data.data.image;
};

export const updateAdminHomepageSliderImage = async (
  id: string,
  payload: Partial<Pick<AdminHomepageSliderImage, 'altText' | 'title' | 'caption' | 'linkUrl' | 'sortOrder' | 'isActive'>>,
): Promise<AdminHomepageSliderImage> => {
  const response = await api.patch<AdminHomepageSliderImageResponse>(`/admin/homepage-slider-images/${id}`, payload);
  return response.data.data.image;
};

export const deleteAdminHomepageSliderImage = async (id: string): Promise<void> => {
  await api.delete(`/admin/homepage-slider-images/${id}`);
};

export const fetchPaperTranslations = async (): Promise<ReadonlyArray<AdminPaperTranslation>> => {
  const response = await api.get<AdminPaperTranslationsResponse>('/admin/translations/papers');
  return response.data.data;
};

export const fetchPrinterTranslations = async (): Promise<ReadonlyArray<AdminPrinterTranslation>> => {
  const response = await api.get<AdminPrinterTranslationsResponse>('/admin/translations/printers');
  return response.data.data;
};

export const updatePaperTranslationManual = async (
  paperId: string,
  locale: string,
  payload: { description: string; status: TranslationStatus }
): Promise<ManualUpdateResponse['data']> => {
  const response = await api.put<ManualUpdateResponse>(`/admin/translations/papers/${paperId}`, {
    locale,
    ...payload,
  });
  return response.data.data;
};

export const updatePrinterTranslationManual = async (
  printerId: string,
  locale: string,
  payload: { description: string; status: TranslationStatus }
): Promise<ManualUpdateResponse['data']> => {
  const response = await api.put<ManualUpdateResponse>(`/admin/translations/printers/${printerId}`, {
    locale,
    ...payload,
  });
  return response.data.data;
};

export const fetchAdminOrder = async (orderId: string): Promise<AdminOrderRefundDetails> => {
  const response = await api.get<{ data: { order: AdminOrderRefundDetails } }>(`/admin/orders/${orderId}`);
  return response.data.data.order;
};

export const fetchAdminUkrposhtaShipmentSummary = async (orderId: string): Promise<AdminUkrposhtaShipmentSummary> => {
  const response = await api.get<{ data: { shipment: AdminUkrposhtaShipmentSummary } }>(`/admin/orders/${orderId}/ukrposhta-summary`);
  return response.data.data.shipment;
};

export const createAdminOrderRefund = async (
  orderId: string,
  request: AdminRefundRequest,
): Promise<AdminRefundResponse> => {
  const response = await api.post<AdminRefundApiResponse>(`/admin/orders/${orderId}/refund`, request);
  return response.data.data;
};
